import { cn } from '@/lib/utils';
import { useAiAssistantStore, useDeployStore, useEditorStore, useUIStore } from '@/store';
import {
    AlertTriangle,
    CheckCircle,
    ChevronDown,
    Clock,
    Copy,
    Droplets,
    ExternalLink,
    Fuel,
    Layers,
    Loader2,
    Play,
    Rocket,
    Shield,
    TestTubes,
    Wand2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CompileOutput from './CompileOutput';
import ContractInteractionPanel from './ContractInteractionPanel';

interface DeployPanelProps {
  workspaceId: string;
}

/* ================================================================ */
/*  Chain metadata — icons, explorers, faucets                      */
/* ================================================================ */

interface ChainMeta {
  id: string;
  name: string;
  chainId: number;
  type: 'testnet' | 'mainnet';
  rpcUrl: string;
  color: string;
  explorer: string;
  faucet?: string;
  symbol: string;
  logo: string; // emoji for now while simple
}

const ALL_CHAINS: ChainMeta[] = [
  // — Ethereum —
  {
    id: 'eth-sepolia',
    name: 'Ethereum Sepolia',
    chainId: 11155111,
    type: 'testnet',
    rpcUrl: '',
    color: '#627EEA',
    explorer: 'https://sepolia.etherscan.io',
    faucet: 'https://sepoliafaucet.com',
    symbol: 'ETH',
    logo: '⟠',
  },
  {
    id: 'eth-mainnet',
    name: 'Ethereum',
    chainId: 1,
    type: 'mainnet',
    rpcUrl: '',
    color: '#627EEA',
    explorer: 'https://etherscan.io',
    symbol: 'ETH',
    logo: '⟠',
  },
  // — Arbitrum —
  {
    id: 'arb-sepolia',
    name: 'Arbitrum Sepolia',
    chainId: 421614,
    type: 'testnet',
    rpcUrl: '',
    color: '#28A0F0',
    explorer: 'https://sepolia.arbiscan.io',
    faucet: 'https://faucet.quicknode.com/arbitrum/sepolia',
    symbol: 'ETH',
    logo: '🔵',
  },
  {
    id: 'arb-one',
    name: 'Arbitrum One',
    chainId: 42161,
    type: 'mainnet',
    rpcUrl: '',
    color: '#28A0F0',
    explorer: 'https://arbiscan.io',
    symbol: 'ETH',
    logo: '🔵',
  },
  // — Polygon —
  {
    id: 'polygon-amoy',
    name: 'Polygon Amoy',
    chainId: 80002,
    type: 'testnet',
    rpcUrl: '',
    color: '#8247E5',
    explorer: 'https://amoy.polygonscan.com',
    faucet: 'https://faucet.polygon.technology',
    symbol: 'POL',
    logo: '🟣',
  },
  {
    id: 'polygon-mainnet',
    name: 'Polygon',
    chainId: 137,
    type: 'mainnet',
    rpcUrl: '',
    color: '#8247E5',
    explorer: 'https://polygonscan.com',
    symbol: 'POL',
    logo: '🟣',
  },
  // — Base —
  {
    id: 'base-sepolia',
    name: 'Base Sepolia',
    chainId: 84532,
    type: 'testnet',
    rpcUrl: '',
    color: '#0052FF',
    explorer: 'https://sepolia.basescan.org',
    faucet: 'https://www.coinbase.com/faucets/base-ethereum-goerli-faucet',
    symbol: 'ETH',
    logo: '🔷',
  },
  {
    id: 'base-mainnet',
    name: 'Base',
    chainId: 8453,
    type: 'mainnet',
    rpcUrl: '',
    color: '#0052FF',
    explorer: 'https://basescan.org',
    symbol: 'ETH',
    logo: '🔷',
  },
  // — Optimism —
  {
    id: 'op-sepolia',
    name: 'Optimism Sepolia',
    chainId: 11155420,
    type: 'testnet',
    rpcUrl: '',
    color: '#FF0420',
    explorer: 'https://sepolia-optimism.etherscan.io',
    faucet: 'https://app.optimism.io/faucet',
    symbol: 'ETH',
    logo: '🔴',
  },
  {
    id: 'op-mainnet',
    name: 'Optimism',
    chainId: 10,
    type: 'mainnet',
    rpcUrl: '',
    color: '#FF0420',
    explorer: 'https://optimistic.etherscan.io',
    symbol: 'ETH',
    logo: '🔴',
  },
  // — Avalanche —
  {
    id: 'avax-fuji',
    name: 'Avalanche Fuji',
    chainId: 43113,
    type: 'testnet',
    rpcUrl: '',
    color: '#E84142',
    explorer: 'https://testnet.snowtrace.io',
    faucet: 'https://faucet.avax.network',
    symbol: 'AVAX',
    logo: '🔺',
  },
  {
    id: 'avax-mainnet',
    name: 'Avalanche',
    chainId: 43114,
    type: 'mainnet',
    rpcUrl: '',
    color: '#E84142',
    explorer: 'https://snowtrace.io',
    symbol: 'AVAX',
    logo: '🔺',
  },
  // — BSC —
  {
    id: 'bsc-testnet',
    name: 'BSC Testnet',
    chainId: 97,
    type: 'testnet',
    rpcUrl: '',
    color: '#F0B90B',
    explorer: 'https://testnet.bscscan.com',
    faucet: 'https://testnet.binance.org/faucet-smart',
    symbol: 'tBNB',
    logo: '🟡',
  },
  {
    id: 'bsc-mainnet',
    name: 'BNB Chain',
    chainId: 56,
    type: 'mainnet',
    rpcUrl: '',
    color: '#F0B90B',
    explorer: 'https://bscscan.com',
    symbol: 'BNB',
    logo: '🟡',
  },
];

export default function DeployPanel({ workspaceId }: DeployPanelProps) {
  const {
    compileResult,
    deployResult,
    deployHistory,
    isCompiling,
    isDeploying,
    isTesting,
    testResult,
    toolchainInfo,
    compile,
    deploy,
    runTests,
    fetchToolchainInfo,
  } = useDeployStore();

  const { tabs, activeTabId, openFiles } = useEditorStore();
  const { fixCompileError, isLoading: isAiLoading } = useAiAssistantStore();
  const { setActivePanel } = useUIStore();

  const [selectedChainId, setSelectedChainId] = useState(11155111);
  const [constructorArgs, setConstructorArgs] = useState('');
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [showTestnets, setShowTestnets] = useState(true);
  const [showMainnets, setShowMainnets] = useState(false);
  const [gasEstimate, setGasEstimate] = useState<string | null>(null);
  const [isFixing, setIsFixing] = useState(false);
  // Mainnet deploy gate — true while waiting for user confirmation
  const [mainnetConfirmPending, setMainnetConfirmPending] = useState(false);

  const activeTab = tabs.find((t) => t.id === activeTabId);

  const selectedChain = useMemo(
    () => ALL_CHAINS.find((c) => c.chainId === selectedChainId),
    [selectedChainId],
  );

  const filteredChains = useMemo(() => {
    const chains: ChainMeta[] = [];
    if (showTestnets) chains.push(...ALL_CHAINS.filter((c) => c.type === 'testnet'));
    if (showMainnets) chains.push(...ALL_CHAINS.filter((c) => c.type === 'mainnet'));
    if (!showTestnets && !showMainnets) return ALL_CHAINS;
    return chains;
  }, [showTestnets, showMainnets]);

  // Sync gasEstimate from compileResult whenever it changes
  useEffect(() => {
    setGasEstimate(compileResult?.gasEstimate ?? null);
  }, [compileResult]);

  // Fetch toolchain info on mount
  useEffect(() => {
    if (workspaceId) fetchToolchainInfo(workspaceId);
  }, [workspaceId, fetchToolchainInfo]);

  const handleCompile = useCallback(async () => {
    // Auto-detect toolchain from workspace info
    const tc = toolchainInfo?.foundry ? 'foundry' : toolchainInfo?.hardhat ? 'hardhat' : undefined;
    await compile(workspaceId, tc);
  }, [workspaceId, compile, toolchainInfo]);

  const handleDeploy = useCallback(async () => {
    if (!compileResult?.success) return;
    const chain = ALL_CHAINS.find((c) => c.chainId === selectedChainId);
    // Mainnet deploy gate — require explicit confirmation
    if (chain?.type === 'mainnet' && !mainnetConfirmPending) {
      setMainnetConfirmPending(true);
      return;
    }
    setMainnetConfirmPending(false);
    const args = constructorArgs.trim() ? JSON.parse(constructorArgs) : undefined;
    await deploy(workspaceId, {
      contract: compileResult.contractName || undefined,
      constructorArgs: args,
      rpcUrl: chain?.rpcUrl || undefined,
      network: chain?.id,
    });
  }, [workspaceId, compileResult, selectedChainId, constructorArgs, deploy]);

  const handleRunTests = useCallback(async () => {
    const tc = toolchainInfo?.foundry ? 'foundry' : toolchainInfo?.hardhat ? 'hardhat' : undefined;
    await runTests(workspaceId, { toolchain: tc });
  }, [workspaceId, runTests, toolchainInfo]);

  /** 🔥 THE MAGIC MOMENT — compile error → AI fix → apply → recompile */
  const handleAiFix = useCallback(async () => {
    if (!compileResult || compileResult.success) return;
    const code = activeTab ? (openFiles.get(activeTab.path) ?? '') : '';
    const errors = compileResult.errors.map((e) => e.message);
    if (!errors.length || !code) return;

    setIsFixing(true);
    setActivePanel('ai'); // Show AI panel so user sees the fix in progress

    await fixCompileError(code, errors, {
      fileName: activeTab?.path,
      code,
    });

    setIsFixing(false);
  }, [compileResult, activeTab, openFiles, fixCompileError, setActivePanel]);

  const copyAddress = useCallback(() => {
    if (deployResult?.contractAddress) {
      navigator.clipboard.writeText(deployResult.contractAddress);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    }
  }, [deployResult]);

  return (
    <div className="h-full flex flex-col bg-[#252526] text-gray-300 overflow-y-auto">
      {/* Header */}
      <div className="px-3 py-2 border-b border-[#3c3c3c] flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
          <Rocket className="w-3.5 h-3.5 text-green-400" />
          Deploy
        </span>
        {selectedChain && (
          <span
            className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
            style={{ backgroundColor: selectedChain.color + '20', color: selectedChain.color }}
          >
            <span>{selectedChain.logo}</span>
            {selectedChain.name}
          </span>
        )}
      </div>

      <div className="p-3 space-y-4">
        {/* Toolchain Info */}
        {toolchainInfo && (
          <div className="flex items-center gap-2 text-[10px] text-gray-500">
            {toolchainInfo.hardhat && (
              <span className="px-1.5 py-0.5 bg-yellow-600/20 text-yellow-400 rounded">
                Hardhat
              </span>
            )}
            {toolchainInfo.foundry && (
              <span className="px-1.5 py-0.5 bg-orange-600/20 text-orange-400 rounded">
                Foundry
              </span>
            )}
            {toolchainInfo.solc && (
              <span className="px-1.5 py-0.5 bg-blue-600/20 text-blue-400 rounded">
                solc {toolchainInfo.solc}
              </span>
            )}
          </div>
        )}

        {/* Compile Section */}
        <div className="space-y-2">
          <h3 className="text-xs font-medium text-gray-400 uppercase flex items-center gap-1.5">
            <Play className="w-3 h-3" />
            Compile
          </h3>
          <button
            onClick={handleCompile}
            disabled={isCompiling}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors',
              'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50',
            )}
          >
            {isCompiling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {isCompiling ? 'Compiling...' : 'Compile'}
          </button>
          {compileResult && <CompileOutput result={compileResult} />}

          {/* 🔥 AI Fix Loop — THE MAGIC MOMENT */}
          {compileResult && !compileResult.success && compileResult.errors.length > 0 && (
            <button
              onClick={handleAiFix}
              disabled={isFixing || isAiLoading}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50"
            >
              {isFixing || isAiLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4" />
              )}
              {isFixing ? 'AI Fixing...' : 'Fix with AI'}
            </button>
          )}
        </div>

        {/* Test Section */}
        <div className="space-y-2 border-t border-[#3c3c3c] pt-3">
          <h3 className="text-xs font-medium text-gray-400 uppercase flex items-center gap-1.5">
            <TestTubes className="w-3 h-3" />
            Tests
          </h3>
          <button
            onClick={handleRunTests}
            disabled={isTesting}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50"
          >
            {isTesting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <TestTubes className="w-4 h-4" />
            )}
            {isTesting ? 'Running Tests...' : 'Run Tests'}
          </button>
          {testResult && (
            <div
              className={cn(
                'p-2 rounded text-xs border',
                testResult.success
                  ? 'bg-green-500/10 border-green-500/20 text-green-300'
                  : 'bg-red-500/10 border-red-500/20 text-red-300',
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                {testResult.success ? (
                  <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                ) : (
                  <Shield className="w-3.5 h-3.5 text-red-400" />
                )}
                <span className="font-medium">
                  {testResult.passed} passed, {testResult.failed} failed
                  {testResult.skipped > 0 && `, ${testResult.skipped} skipped`}
                </span>
              </div>
              {testResult.output && (
                <pre className="mt-1 text-[10px] text-gray-400 bg-[#1e1e1e] p-2 rounded max-h-32 overflow-auto whitespace-pre-wrap">
                  {testResult.output}
                </pre>
              )}
            </div>
          )}
        </div>

        {/* Deploy Section */}
        {compileResult?.success && (
          <div className="space-y-3 border-t border-[#3c3c3c] pt-4">
            <h3 className="text-xs font-medium text-gray-400 uppercase flex items-center gap-1.5">
              <Rocket className="w-3 h-3" />
              Deploy
            </h3>

            {/* Compiled artifacts */}
            {compileResult.artifacts && compileResult.artifacts.length > 0 && (
              <details className="group">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 flex items-center gap-1">
                  <Layers className="w-3 h-3" />
                  Artifacts ({compileResult.artifacts.length} files)
                  <ChevronDown className="w-3 h-3 transition-transform group-open:rotate-180" />
                </summary>
                <div className="mt-1 text-[10px] text-gray-500 bg-[#1e1e1e] p-2 rounded max-h-32 overflow-auto space-y-0.5">
                  {compileResult.artifacts.map((a, i) => (
                    <div key={i} className="font-mono">
                      {a}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* ABI preview — only if available */}
            {compileResult.abi && (
              <details className="group">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 flex items-center gap-1">
                  <Layers className="w-3 h-3" />
                  ABI ({compileResult.abi.length} entries)
                  <ChevronDown className="w-3 h-3 transition-transform group-open:rotate-180" />
                </summary>
                <pre className="mt-1 text-[10px] text-gray-500 bg-[#1e1e1e] p-2 rounded max-h-32 overflow-auto">
                  {JSON.stringify(compileResult.abi, null, 2)}
                </pre>
              </details>
            )}

            {/* Chain filter toggles */}
            <div className="flex gap-1.5">
              <button
                onClick={() => setShowTestnets(!showTestnets)}
                className={cn(
                  'px-2 py-1 text-[10px] rounded transition-colors',
                  showTestnets
                    ? 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/40'
                    : 'bg-[#3c3c3c] text-gray-500',
                )}
              >
                Testnets
              </button>
              <button
                onClick={() => setShowMainnets(!showMainnets)}
                className={cn(
                  'px-2 py-1 text-[10px] rounded transition-colors',
                  showMainnets
                    ? 'bg-green-600/20 text-green-400 border border-green-600/40'
                    : 'bg-[#3c3c3c] text-gray-500',
                )}
              >
                Mainnets
              </button>
            </div>

            {/* Target chain */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Target Chain</label>
              <select
                value={selectedChainId}
                onChange={(e) => setSelectedChainId(Number(e.target.value))}
                className="w-full bg-[#3c3c3c] text-gray-200 text-xs rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-blue-500"
              >
                {filteredChains.map((c) => (
                  <option key={c.id} value={c.chainId}>
                    {c.logo} {c.name} ({c.type}) — {c.symbol}
                  </option>
                ))}
              </select>
            </div>

            {/* Chain info bar */}
            {selectedChain && (
              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                <a
                  href={selectedChain.explorer}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-0.5 hover:text-blue-400 transition-colors"
                >
                  <ExternalLink className="w-2.5 h-2.5" /> Explorer
                </a>
                {selectedChain.faucet && (
                  <a
                    href={selectedChain.faucet}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-0.5 hover:text-blue-400 transition-colors"
                  >
                    <Droplets className="w-2.5 h-2.5" /> Faucet
                  </a>
                )}
                {selectedChain.type === 'mainnet' && (
                  <span className="flex items-center gap-0.5 text-yellow-500">
                    <Shield className="w-2.5 h-2.5" /> Real funds
                  </span>
                )}
              </div>
            )}

            {/* Constructor args */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Constructor Args (JSON array)
              </label>
              <input
                type="text"
                value={constructorArgs}
                onChange={(e) => setConstructorArgs(e.target.value)}
                className="w-full bg-[#3c3c3c] text-gray-200 text-xs rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-blue-500"
                placeholder='["arg1", 100]'
              />
            </div>

            {/* Gas Estimate */}
            {gasEstimate && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#1e1e1e] rounded text-[10px]">
                <Fuel className="w-3 h-3 text-yellow-400" />
                <span className="text-gray-400">Est. gas:</span>
                <span className="text-yellow-300 font-mono">{gasEstimate}</span>
              </div>
            )}

            {/* Mainnet deploy confirmation gate */}
            {mainnetConfirmPending && (
              <div className="rounded border border-yellow-600 bg-yellow-950/40 p-3 space-y-2 text-xs">
                <p className="text-yellow-300 font-medium flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Mainnet deployment — this is irreversible
                </p>
                <p className="text-gray-400">
                  You are about to deploy to{' '}
                  <span className="text-yellow-300">{selectedChain?.name}</span>. Real funds may be
                  spent. Are you sure?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDeploy}
                    className="flex-1 px-2 py-1.5 rounded bg-yellow-600 hover:bg-yellow-700 text-white font-medium"
                  >
                    Yes, deploy to mainnet
                  </button>
                  <button
                    onClick={() => setMainnetConfirmPending(false)}
                    className="flex-1 px-2 py-1.5 rounded bg-[#3c3c3c] hover:bg-[#4a4a4a] text-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Deploy button */}
            <button
              onClick={handleDeploy}
              disabled={isDeploying || mainnetConfirmPending}
              className={cn(
                'w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50',
                selectedChain?.type === 'mainnet'
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white',
              )}
            >
              {isDeploying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Rocket className="w-4 h-4" />
              )}
              {isDeploying
                ? 'Deploying...'
                : selectedChain?.type === 'mainnet'
                  ? `Deploy to ${selectedChain.name} (Mainnet)`
                  : 'Deploy Contract'}
            </button>
          </div>
        )}

        {/* Deploy Result */}
        {deployResult && (
          <div
            className={cn(
              'space-y-2 border-t border-[#3c3c3c] pt-4',
              deployResult.success ? 'text-green-300' : 'text-red-300',
            )}
          >
            <h3 className="text-xs font-medium text-gray-400 uppercase">Result</h3>
            {deployResult.success ? (
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                  <span className="text-green-300">Deployed successfully</span>
                </div>
                {deployResult.contractAddress && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">Address:</span>
                    <code className="text-green-300 bg-[#1e1e1e] px-1 rounded text-[10px] truncate max-w-[140px]">
                      {deployResult.contractAddress}
                    </code>
                    <button onClick={copyAddress} className="p-0.5 hover:bg-[#3c3c3c] rounded">
                      {copiedAddress ? (
                        <CheckCircle className="w-3 h-3 text-green-400" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-400" />
                      )}
                    </button>
                    {selectedChain && (
                      <a
                        href={`${selectedChain.explorer}/address/${deployResult.contractAddress}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-0.5 hover:bg-[#3c3c3c] rounded text-gray-400 hover:text-blue-400"
                        title="View on explorer"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}
                {deployResult.transactionHash && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">Tx: </span>
                    <code className="text-gray-300 text-[10px] truncate max-w-[140px]">
                      {deployResult.transactionHash}
                    </code>
                    {selectedChain && (
                      <a
                        href={`${selectedChain.explorer}/tx/${deployResult.transactionHash}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-0.5 hover:bg-[#3c3c3c] rounded text-gray-400 hover:text-blue-400"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}
                {deployResult.gasUsed && (
                  <div className="flex items-center gap-1">
                    <Fuel className="w-3 h-3 text-yellow-400" />
                    <span className="text-gray-400">Gas: </span>
                    <span>{deployResult.gasUsed}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-red-400">{deployResult.error}</p>
            )}
          </div>
        )}

        {/* Deploy History */}
        {deployHistory.length > 0 && (
          <div className="border-t border-[#3c3c3c] pt-4">
            <h3 className="text-xs font-medium text-gray-400 uppercase mb-2">History</h3>
            <div className="space-y-1.5">
              {deployHistory.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-[#1e1e1e]">
                  {item.success ? (
                    <CheckCircle className="w-3 h-3 text-green-400 shrink-0" />
                  ) : (
                    <Clock className="w-3 h-3 text-red-400 shrink-0" />
                  )}
                  <span className="truncate text-gray-400">
                    {item.contractAddress
                      ? `${item.contractAddress.slice(0, 10)}...${item.contractAddress.slice(-6)}`
                      : (item.error ?? 'Failed')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Contract Interaction */}
        {deployResult?.success && deployResult.contractAddress && compileResult?.success && (
          <div className="border-t border-[#3c3c3c] pt-4">
            <h3 className="text-xs font-medium text-gray-400 uppercase mb-2">Interact</h3>
            <ContractInteractionPanel />
          </div>
        )}
      </div>
    </div>
  );
}
